import { useState, useRef, useEffect, useCallback } from 'react';

let _msgId = 0;

/**
 * Toast notification hook — auto-dismissing stacked messages.
 *
 * Messages stack vertically (newest on top). Each message has its own
 * auto-dismiss timer and can be manually dismissed. Identical messages
 * (same type + text) are deduped — re-firing one resets its timer rather
 * than stacking a duplicate — and the stack is capped at `maxMessages`
 * (oldest dropped first).
 *
 * @param {number} [defaultDuration=1500] — ms before a message auto-dismisses (0 = sticky)
 * @param {number} [maxMessages=3] — max messages shown at once
 * @returns {[messages: Array<{id: number, type: string, message: string}>, toast: Function, dismiss: Function]}
 */
export function useToast(defaultDuration = 1500, maxMessages = 3) {
  const [messages, setMessages] = useState([]);
  const timersRef = useRef({});     // id → timeout
  const messagesRef = useRef([]);   // authoritative list; mirrors `messages` for synchronous dedup/cap

  // Clear a message's pending auto-dismiss timer, if any.
  const disarmTimer = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const dismiss = useCallback((id) => {
    disarmTimer(id);
    messagesRef.current = messagesRef.current.filter(m => m.id !== id);
    setMessages(messagesRef.current);
  }, [disarmTimer]);

  const toast = useCallback((type, message, duration) => {
    // Error/warning types get longer display time unless explicitly overridden
    const resolvedDuration = duration != null
      ? duration
      : (type === 'error' || type === 'warning') ? 4000 : defaultDuration;

    const prev = messagesRef.current;

    // Dedup: same type + text already showing → reset its timer, don't stack.
    const dup = prev.find(m => m.type === type && m.message === message);
    if (dup) {
      disarmTimer(dup.id);
      if (resolvedDuration > 0) {
        timersRef.current[dup.id] = setTimeout(() => dismiss(dup.id), resolvedDuration);
      }
      return;
    }

    const id = ++_msgId;
    const next = [{ id, type, message }, ...prev];   // newest on top

    // Cap: keep the newest `maxMessages`, drop + disarm the oldest.
    next.slice(maxMessages).forEach(m => disarmTimer(m.id));

    messagesRef.current = next.slice(0, maxMessages);
    setMessages(messagesRef.current);

    if (resolvedDuration > 0) {
      timersRef.current[id] = setTimeout(() => dismiss(id), resolvedDuration);
    }
  }, [defaultDuration, dismiss, disarmTimer, maxMessages]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  return [messages, toast, dismiss];
}

/**
 * Request-lock hook — prevents concurrent operations by locking the UI.
 *
 * Returns `isLocked` boolean and `runLocked(asyncFn)` wrapper.
 * While locked, all user interaction is blocked by LoadingOverlay.
 *
 * @returns {{ isLocked: boolean, runLocked: (fn: () => Promise<any>) => Promise<any> }}
 */
export function useRequestLock() {
  const [isLocked, setIsLocked] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const runLocked = useCallback(async (fn) => {
    setIsLocked(true);
    try {
      return await fn();
    } finally {
      if (mountedRef.current) {
        setIsLocked(false);
      }
    }
  }, []);

  return { isLocked, runLocked };
}
